import { ButtonGroup } from "@xola/ui-kit";
import { JsDateRange, now } from "common";
import React, { useState } from "react";
import { Affiliate, Partner, SelectionState } from "types";
import shallow from "zustand/shallow";
import { useUser } from "../../../hooks/useAccount";
import { getInitialDate } from "../../../models/Reports";
import { isAffiliate } from "../../../models/User";
import {
    AffiliatePaymentStatus,
    usePartnerCommissionsFilterStore,
} from "../../../stores/reports/partnerCommissionsFilterStore";
import { ReportDatePicker } from "../ReportDatePicker";
import { ReportSelection } from "../ReportSelection";
import { useCollection } from "@/hooks/useCollection";

export const PartnerCommissionsFilter = ({ partners }: { partners?: Partner[] }) => {
    const user = useUser();
    const {
        data: [loggedInAffiliate],
    } = useCollection<Affiliate>(
        "affiliates",
        { user: user.id, limit: 1, status: "active" },
        { enabled: isAffiliate(user) },
    );

    const userIsAffiliateChild = Boolean(loggedInAffiliate?.parent);

    const [from, to] = usePartnerCommissionsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [setFrom, setTo] = usePartnerCommissionsFilterStore((state) => [state.setFrom, state.setTo], shallow);
    const [filters, setFilters] = usePartnerCommissionsFilterStore(
        (state) => [state.filters, state.setFilters],
        shallow,
    );

    const [value, setValue] = useState(getInitialDate(from, to));
    const handleDateChange = (dateValue: JsDateRange) => {
        if (dateValue.from && dateValue.to) {
            setFrom(now(dateValue.from));
            setTo(now(dateValue.to));
        }

        setValue(dateValue);
    };

    const activeStatus = filters.affiliatePaymentStatus === AffiliatePaymentStatus.ALL ? 0 : 1;
    const handlePaymentStatusChange = (activeIndex: number) => {
        const affiliatePaymentStatus = activeIndex === 0 ? AffiliatePaymentStatus.ALL : AffiliatePaymentStatus.PENDING;
        setFilters({ affiliatePaymentStatus });
    };

    const handlePartnerChange = (value: SelectionState) => {
        setFilters({ ...filters, partners: value });
    };

    return (
        <div className="flex flex-row items-center justify-between border-b border-gray-light px-6 py-3">
            <div className="flex w-full flex-row items-center justify-between">
                <div
                    className="flex w-full flex-row items-center justify-start space-x-1 xl:space-x-4"
                    data-testid="earning-report-filter"
                >
                    <div>
                        <ReportDatePicker from={from} to={to} value={value} max={now()} onChange={handleDateChange} />
                    </div>
                    {!userIsAffiliateChild && (
                        <div>
                            <ReportSelection
                                label="Partners"
                                title="Filter Partners"
                                data={partners ?? []}
                                value={filters.partners}
                                onChange={handlePartnerChange}
                            />
                        </div>
                    )}
                </div>

                {isAffiliate(user) ? null : (
                    <div>
                        <ButtonGroup
                            className="align-top"
                            size="medium"
                            value={activeStatus}
                            onChange={handlePaymentStatusChange}
                        >
                            <ButtonGroup.Button>All</ButtonGroup.Button>
                            <ButtonGroup.Button>Pending</ButtonGroup.Button>
                        </ButtonGroup>
                    </div>
                )}
            </div>
        </div>
    );
};
